import useInit from './hooks/useInit'
import React, { ComponentType, useCallback, useMemo, useState } from 'react'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { apiUrls, tableColumnWidths } from 'consts'
import {
  SearchState,
  IntegratedFiltering,
  SortingState,
  TableColumnWidthInfo,
  Table as TableBase,
  PagingState,
  IntegratedPaging,
  Column,
  EditingState,
  ChangeSet,
  DataTypeProvider,
  Filter,
  SelectionState,
  IntegratedSelection,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  TableInlineCellEditing,
  TableSelection,
  Toolbar,
} from '@devexpress/dx-react-grid-bootstrap4'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import getRows from './utils/getRows'
import DeleteModal from 'shared/components/DeleteModal'
import setDefaultFilterValue from './utils/setDefaultFilterValue'
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import SortableItem from 'components/SortableItem'
import reorderRows from 'utils/reorderRows'
import useReorder from 'hooks/useReorder'
import SortableTableCell from 'components/SortableTableCell'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import SortableOverLayContent from 'components/SortableOverlayContent'
import AddFilterValueForm from 'components/AddFilterValueForm'
import sortByAlphabet from './utils/sortByAlphabet'
import { NavLink } from 'react-router-dom'
import { IonIcon } from '@ionic/react'
import { home } from 'ionicons/icons'
import ActionButtons from 'shared/components/ActionButtons'
import formatOrder from './utils/formatOrder'
import formatDefault from './utils/formatDefault'
import filterByDefaultValue from './utils/filterByDefaultValue'
import filterByOrder from './utils/filterByOrder'
import updateRowInline from './utils/updateRowInline'
import * as Icon from 'react-bootstrap-icons'
import bulkDelete from './utils/bulkDelete'
import filterBySynced from './utils/filterBySynced'
import formatSynced from './utils/formatSynced'
import formatLabel from './utils/formatLabel'

interface ManageFilterValuesProps {
  filterId: string
  isMulti: boolean
  setShowOverlay: Function
  setFilterValuesLoaded: Function
  unCheckFilterDefault: Function
}

const ManageFilterValues: React.FunctionComponent<ManageFilterValuesProps> = (
  props: ManageFilterValuesProps,
) => {
  const [rows, setRows] = useState<any>([])
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [buttonClickCount, setButtonClickCount] = useState<number>(0)
  const [dragEndEvent, setDragEndEvent] = useState<any | null>(null)
  const [rowChanges, setRowChanges] = useState<any>({})
  const [validationStatus, setValidationStatus] = useState<any>({})
  const sortableItems = useMemo(() => rows?.map(({ id }) => id), [rows])

  const [selectedFilterValues, setSelectedFilterValues] = useState<{}>([])
  const [selectedFilterValuesIndexes, setSelectedFilterValuesIndexes] = useState<any>([])
  const [bulkActionsEnabled, setBulkActionsEnabled] = useState<boolean>(false)
  const [isBulkDeleteLoading, setIsBulkDeleteLoading] = useState<boolean>(false)
  const [bulkDeleteCunter, setBulkDeleteCunter] = useState<number>(0)

  const [inlineEditableColumns] = useState<Array<string>>(['order', 'label', 'value', 'parent_value'])

  const columns: Column[] = [
    { name: 'sortColumn', title: ' ' },
    { name: 'order', title: '#'},
    { name: 'type', title: 'Type' },
    { name: 'label', title: 'Label' },
    { name: 'value', title: 'Value' },
    { name: 'parent_value', title: 'Parent Value' },
    { name: 'synced', title: 'Synced' },
    { name: 'is_default', title: 'Default' },
    { name: 'action', title: 'Actions' },
  ]

  const columnWidths: TableColumnWidthInfo[] = [
    { columnName: 'sortColumn', width: tableColumnWidths.sortColumn },
    { columnName: 'order', width: tableColumnWidths.sortColumn },
    { columnName: 'type', width: tableColumnWidths.action },
    { columnName: 'label', width: 'auto' },
    { columnName: 'value', width: 'auto' },
    { columnName: 'parent_value', width: 'auto' },
    { columnName: 'synced', width: 'auto' },
    { columnName: 'is_default', width: tableColumnWidths.active },
    { columnName: 'action', width: tableColumnWidths.action },
  ]

  const [editingStateColumnExtensions] = useState<Array<EditingState.ColumnExtension>>([
    { columnName: 'sortColumn', editingEnabled: false },
    { columnName: 'order', editingEnabled: true },
    { columnName: 'type', editingEnabled: false },
    { columnName: 'label', editingEnabled: true },
    { columnName: 'value', editingEnabled: true },
    { columnName: 'parent_value', editingEnabled: true },
    { columnName: 'synced', editingEnabled: false },
    { columnName: 'is_default', editingEnabled: false },
    { columnName: 'action', editingEnabled: false },
  ])

  const [integratedFilteringColumnExtensions] = useState<Array<IntegratedFiltering.ColumnExtension>>([
    {
      columnName: 'is_default',
      predicate: (value: any, filter: Filter, row: any) => {
        return filterByDefaultValue(value, filter, row)
      },
    },
    {
      columnName: 'synced',
      predicate: (value: any, filter: Filter, row: any) => {
        return filterBySynced(value, filter, row)
      },
    },
    {
      columnName: 'order',
      predicate: (value: any, filter: Filter, row: any) => {
        return filterByOrder(value, filter, row)
      },
    },
    {
      columnName: 'action',
      predicate: (_) => {
        return false
      },
    },
  ])


  const [labelFormatter] = useState<any>({
    for: ['label'],
    formatterComponent: formatLabel,
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const [orderFormatter] = useState<any>({
    for: ['order'],
    formatterComponent: formatOrder,
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const [defaultFormatter] = useState<any>({
    for: ['is_default'],
    formatterComponent: formatDefault,
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const [syncedFormatter] = useState<any>({
    for: ['synced'],
    formatterComponent: formatSynced,
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const handleSetDefault = (rowId) => {
    setDefaultFilterValue({
      id: rowId,
      setButtonClickCount: setButtonClickCount,
      unCheckFilterDefault: props.unCheckFilterDefault,
      setDragEndEvent: setDragEndEvent
    })
  }

  const actionFormatterFunc = useCallback(
    ({ row }) => {
      const buttons: Array<any> = []
  
      if (row.is_default !== 1) {
        buttons.push({
          content: (
            <NavLink
              key={'remove-button' + row.id}
              className='info'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSetDefault(row.id)
              }}
              to='#'
              title={'Set as Default'}
            >
              <IonIcon icon={home} className='ionicon' style={{ fontSize: '16px' }} />
            </NavLink>
          ),
        })
      }
  
      buttons.push({
        type: 'd',
        id: row.id,
        url: '/delete-filter-value/' + row.id,
        setDeleteEntryId,
        setShowModal,
      })
  
      return <ActionButtons buttons={buttons} />
    },
    [handleSetDefault, setDeleteEntryId, setShowModal]
  )

  const [actionFormatter] = useState<any>({
    for: ['action'],
    formatterComponent: actionFormatterFunc,
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })


  const getRowsConfigured = () => {
    return getRows({
      filterId: props.filterId,
      setDeleteEntryId,
      setShowModal,
      handleSetDefault,
    })
  }

  const setRowsConfigured = (rows) => {
    //if row order changed by drag & drop remove all errors from inline field editting
    setValidationStatus({})
    setRowChanges({})
    setRows(rows)
  }

  useInit({
    setRows: setRowsConfigured,
    getRows: getRowsConfigured,
    setFilterValuesLoaded: props.setFilterValuesLoaded,
    buttonClickCount,
    bulkDeleteCunter,
  })

  useReorder({
    rows: rows,
    dragEndEvent,
    setRows: setRowsConfigured,
    sortRowsUrl: apiUrls.filterValues + '/' + apiUrls.actionSort,
    reorderRows,
  })

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}))

  const [tableMessages] = useState<TableBase.LocalizationMessages>({
    noData: 'No Filter Values found',
  })

  const TableRow: ComponentType<any> | undefined = ({ row, ...restProps }) => {
    const rowClass : string | undefined = row.is_included_in_permissions ? undefined : 'not-included-in-permissions'
    return (
      <SortableItem key={row.id} index={rows.indexOf(row)} row={row} enableSorting={!bulkActionsEnabled} rowClass={rowClass} {...restProps} />
    )
  }


  const sortByAlphabetAction = () => {
    return sortByAlphabet({
      rows: rows,
      sortableProperty: 'label',
      setRows: setRows,
      sortRowsUrl: apiUrls.filterValues + '/' + apiUrls.actionSort,
      reorderRows,
    })
  }

  const SortByAlphabetButton = (props) => {
    return rows.length > 1 ? (
      <a href='#/' className='small' onClick={sortByAlphabetAction}>
        Sort by alphabet
      </a>
    ) : (
      <></>
    )
  }

  const TableComponent = ({ ...restProps }) => (
    <>
      <SortByAlphabetButton />
      <Table.Table {...restProps} className='with-action min-mobile-width' />
    </>
  )

  const SearchPanelInputComponent = (props) => {
    return SearchPanelInput({
      inputProps: props,
    })
  }

  const PagingContainer = (props) => {
    return <PagingPanel.Container {...props} className='table-footer' />
  }

  const handleBulkSelectionChanged = (selectedData) => {
    setSelectedFilterValuesIndexes(selectedData)
    setBulkActionsEnabled(selectedData.length > 0)
    setSelectedFilterValues(rows.filter((row, index) => selectedData.includes(index)))
  }


const validationRules = {
  order: {
    isValid: o => {
      return !Number.isNaN(o) && o >= 0 && o < sortableItems.length
     },
    errorText: `Order must be a positive integer lower than ${sortableItems.length + 1}.`,
  },
  label: {
    isValid: l => {
      return (l?.trim()?.length) > 0
    },
    errorText: 'Label is a required field',
  },
  value: {
    isValid: v => {
      return true
    },
    errorText: 'Value: An error occurred',
  },
  parent_value: {
    isValid: v => {
      return true
    },
    errorText: 'Parent Value: An error occurred',
  },
}

const validate = (changed, validationStatus) => Object.keys(changed).reduce((status, id) => {
  let rowStatus = validationStatus[id] || {}
  if (changed[id]) {
    rowStatus = {
      ...rowStatus,
      ...Object.keys(changed[id]).reduce((acc, field) => {
        const isValid : boolean = validationRules[field]?.isValid(changed[id][field]) ?? true
        return {
          ...acc,
          [field]: {
            isValid,
            error: !isValid && validationRules[field].errorText,
          },
        }
      }, {}),
    }
  }

  return { ...status, [id]: rowStatus }
}, {})


  const commitChanges = (changes: ChangeSet) => {
    const changed = changes.changed
    if (changed !== undefined) {
      Object.keys(changed).forEach(key => {
        if (changed[key] !== undefined) {
          if(changed[key]?.order !== undefined){
            const newOrder = !isNaN(changed[key]?.order) ? (parseInt(changed[key].order, 10) - 1) : 'Invalid Value'
            const isValid = validationRules.order.isValid(newOrder)
            if(isValid){
              const id = rows[key]?.id
              const orginalId = rows[newOrder]?.id
              changed[key].order = newOrder
              if(orginalId !== null && id !== null){
                setDragEndEvent({active: {id: id}, over: {id: orginalId}})
              }
            }
            changed[key].order = newOrder
          } 
          else {
            const field = inlineEditableColumns.find(f => changed[key]?.[f] !== undefined)


            if (field) {
              const isValid = validationRules[field]?.isValid(changed[key][field])

              const newValue = isValid ? changed[key][field] : 'Invalid Value'

              if (isValid) {
                const id = rows[key]?.id
                changed[key][field] = newValue
                
                const updateObj = {
                  ...rows[key],
                  [field]: newValue,
                }

                updateRowInline({
                  row: updateObj,
                  setRows: setRows,
                  setShowOverlay: props.setShowOverlay
                })
              } else {
                changed[key][field] = newValue
              }
            }
          }
        }
      })
      setValidationStatus({ ...validationStatus, ...validate(changed, validationStatus) })
    }
  }

  const TableCell: any = ({ value, ...rest }) => {
    return (
      <SortableTableCell value={value} tableCellProps={rest} draggableColumnName='sortColumn' />
    )
  }

  const Cell = React.useCallback((props) => {
    const { tableRow: { rowId }, column: { name: columnName } } = props
    const columnStatus = validationStatus[rowId]?.[columnName]
    const valid : boolean = !columnStatus || columnStatus.isValid
    const style = {
      //hacky solution to prevent clicks on unchangeable cells
      ...(!inlineEditableColumns.includes(props.column.name) ? { pointerEvents: 'none' as const } : {}),
    }
    const className : string = valid ? '' : 'invalid-inline-input'
    const errorMsg : string = valid ? '' : validationStatus[rowId][columnName].error

    const { tableRow, tableColumn, column, row, ...filteredProps } = props

    return valid? (
      <TableCell
        key={'cell' + props.row.id}
        {...props}
        style={style}
        className={className}
      />
    ) : (
      <OverlayTrigger
        key={props.row.id}
        placement='right'
        delay={{ show: 100, hide: 100 }}
        overlay={
          <Tooltip>{errorMsg}</Tooltip>
        }
      >
        {({ ref, ...triggerHandler }) => (
          <td key={'cell-readonly' + props.row.id} ref={ref} style={style} className={'w-100 dx-g-bs4-table-cell text-nowrap ' + className} {...triggerHandler}> 
         <span
          {...filteredProps}
        />
        </td>
    )}
      </OverlayTrigger>
    )
  }, [validationStatus])

  const TableInlineEditingCell = ({ column, row, value, ...rest }) => {
    const isEditable = inlineEditableColumns.includes(column.name)
    let formattedValue = value
    if(column.name === 'order'){
      formattedValue = formatOrder({value: value})
    }
    else if(column.name === 'is_default'){
      formattedValue = formatDefault({value: formattedValue})
    }
    else if(column.name === 'synced'){
      formattedValue = formatSynced({value: formattedValue})
    }
    else if(column.name === 'action'){
      formattedValue = actionFormatterFunc({row: row})
    }
    
    const { editingEnabled, onFocus, onBlur, onKeyDown, tableColumn, tableRow, onValueChange, ...restProps } = rest
    return <TableInlineCellEditing.Cell autoFocus onFocus={onFocus} 
                                        onBlur={onBlur} 
                                        onKeyDown={onKeyDown} 
                                        tableColumn={tableColumn} 
                                        tableRow={tableRow} 
                                        className={isEditable ? '' : 'cell-is-not-editable'}
                                         editingEnabled={isEditable} 
                                         onValueChange={onValueChange}
                                         column={column} 
                                         row={row} 
                                         value={formattedValue} 
                                         {...restProps} 
            />
  }

  return (
    <>
      <h4 className='mb-4'>Add Filter Value</h4>
      <div id='manageFilterValues'>
        <AddFilterValueForm
          filterId={props.filterId}
          isMulti={props.isMulti}
          setShowOverlay={props.setShowOverlay}
          setButtonClickCount={setButtonClickCount}
        />
        <Row>
          <Col md={12}>
            <h4 className='mb-4'>Filter Values</h4>
            <div className='d-flex flex-column overflow-auto' style={{ maxHeight: '750px' }}>
              <DndContext
                sensors={sensors}
                onDragEnd={(e) => setDragEndEvent(e)}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                  <Grid rows={rows} columns={columns}>
                    <DataTypeProvider {...labelFormatter} />
                    <DataTypeProvider {...orderFormatter} />
                    <DataTypeProvider {...defaultFormatter} />
                    <DataTypeProvider {...syncedFormatter} />
                    <DataTypeProvider {...actionFormatter} />
                    <SortingState columnSortingEnabled={false} defaultSorting={[{ columnName: 'order', direction: 'asc' }]} />
                    <SearchState defaultValue='' />
                    <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />
                    <SelectionState
                      selection={selectedFilterValuesIndexes}
                      onSelectionChange={(selectedData) => handleBulkSelectionChanged(selectedData)}
                    />
                    <PagingState defaultCurrentPage={0} pageSize={15} />
                    <IntegratedPaging />
                    <IntegratedSelection />
                    <Toolbar />
                    <SearchPanel inputComponent={SearchPanelInputComponent} />
                    <EditingState
                      rowChanges={rowChanges}
                      onRowChangesChange={setRowChanges}
                      onCommitChanges={commitChanges}
                      columnExtensions={editingStateColumnExtensions}
                    />
                    <Table
                      tableComponent={TableComponent}
                      cellComponent={Cell}
                      rowComponent={TableRow}
                      messages={tableMessages}
                    />
                    <TableSelection showSelectAll={true} />
                    <TableColumnResizing
                      defaultColumnWidths={columnWidths}
                      resizingMode='nextColumn'
                    />
                    <TableHeaderRow showSortingControls />
                    <TableInlineCellEditing selectTextOnEditStart={false} cellComponent={TableInlineEditingCell} />
                    <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />
                  </Grid>

                  <div className='bottom-buttons'>
                    <Button
                      disabled={!bulkActionsEnabled || isBulkDeleteLoading}
                      onClick={(e) =>
                        bulkDelete({
                          selectedFilterValues,
                          setShowOverlay: props.setShowOverlay,
                          setBulkActionsEnabled,
                          setIsBulkDeleteLoading,
                          setBulkDeleteCunter,
                          handleBulkSelectionChanged
                        })
                      }
                      variant='primary'
                      size='sm'
                      className='float-right'
                    >
                      {isBulkDeleteLoading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          &nbsp;
                        </>
                      ) : (
                        <Icon.Trash />
                      )}
                      &nbsp; Delete
                    </Button>
                  </div>

                  <DeleteModal
                    url={apiUrls.filterValues}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    deleteEntryId={deleteEntryId}
                    setDeleteEntryId={setDeleteEntryId}
                    entityName='Filter Value'
                    getRows={getRowsConfigured}
                    setRows={setRowsConfigured}
                    scrollTop={false}
                    customConfirmMessage={{
                      rows,
                      confirmMessage: (row) => {
                        let confirmMessage = 'Are you sure you want to delete this Filter Value?'

                        const includedEntites: Array<string> = []

                        if (row.is_included_in_permissions) {
                          includedEntites.push('Permission(s)')
                        }

                        if (includedEntites.length > 0) {
                          confirmMessage +=
                            ' Note that this Filter Value is also included in ' +
                            includedEntites.join(' and ') +
                            '.'
                        }

                        return confirmMessage
                      },
                    }}
                  />
                </SortableContext>
                <DragOverlay>
                  <SortableOverLayContent />
                </DragOverlay>
              </DndContext>
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default ManageFilterValues
